import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  MagnifyingGlassIcon, 
  ShieldCheckIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../../../stores/authStore';
import { api } from '../../../services/api';
import toast from 'react-hot-toast';

interface Food {
  _id: string;
  legacyId?: number;
  name: string;
  description?: string;
  brand?: string;
  category?: string;
  servingSize?: number;
  servingMeasure?: string;
  calories?: number;
  protein?: number;
  fat?: number;
  totalCarbs?: number;
  dietaryFiber?: number;
  sugars?: number;
  approved?: boolean;
  isPublic?: boolean;
  isVegan?: boolean;
  isVegetarian?: boolean;
  isGlutenFree?: boolean;
  isDairyFree?: boolean;
  allergens?: string[];
  tags?: string[];
  createdBy?: string;
  updatedBy?: string;
  deletedBy?: string;
  deletedAt?: string;
  createdAt: string;
  updatedAt: string;
}

// Cache for foods data
let foodsCache: Food[] | null = null;
let foodsCacheTimestamp: number | null = null;
let foodsPromise: Promise<Food[]> | null = null; // Prevent duplicate requests
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const FoodLibrary: React.FC = () => {
  const { user } = useAuthStore();
  const [searchParams] = useSearchParams();
  const [allFoods, setAllFoods] = useState<Food[]>([]);
  const [filteredFoods, setFilteredFoods] = useState<Food[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<Food | null>(null);

  const isConsultant = user?.roles?.includes('CONSULTANT');

  // Fetch all foods with caching and duplicate request prevention
  const fetchAllFoods = useCallback(async () => {
    // Check if we have valid cached data
    const now = Date.now();
    if (foodsCache && foodsCacheTimestamp && (now - foodsCacheTimestamp) < CACHE_DURATION) {
      setAllFoods(foodsCache);
      setFilteredFoods(foodsCache);
      setLoading(false);
      return;
    }

    // If there's already a request in progress, wait for it
    if (foodsPromise) {
      try {
        const foods = await foodsPromise;
        setAllFoods(foods);
        setFilteredFoods(foods);
        setLoading(false);
        return;
      } catch (error) {
        // If the existing promise failed, we'll make a new request below
        foodsPromise = null;
      }
    }

    try {
      setLoading(true);
      
      // Create a new promise for this request
      foodsPromise = api.get('/libraries/foods').then((response: any) => {
        const foods = response.data.foods || [];
        
        // Update cache
        foodsCache = foods;
        foodsCacheTimestamp = now;
        foodsPromise = null; // Clear the promise
        
        return foods;
      });
      
      const foods = await foodsPromise;
      setAllFoods(foods || []);
      setFilteredFoods(foods || []);
    } catch (error) {
      console.error('Failed to fetch foods:', error);
      toast.error('Failed to load foods');
      setAllFoods([]);
      setFilteredFoods([]);
      foodsPromise = null; // Clear the promise on error
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchAllFoods();
  }, [fetchAllFoods]);

  // Client-side filtering
  useEffect(() => {
    if (!searchTerm || searchTerm.length < 3) {
      setFilteredFoods(allFoods);
      return;
    }

    const filtered = allFoods.filter(item =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.category?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredFoods(filtered);
  }, [searchTerm, allFoods]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    // Update URL for meaningful searches
    if (value.length === 0 || value.length >= 3) {
      const newParams = new URLSearchParams(searchParams);
      if (value && value.length >= 3) {
        newParams.set('search', value);
      } else {
        newParams.delete('search');
      }
      window.history.replaceState({}, '', `${window.location.pathname}?${newParams}`);
    }
  };

  const toggleExpanded = (itemId: string) => {
    if (expandedItem === itemId) {
      setExpandedItem(null);
      setEditingItem(null);
    } else {
      setExpandedItem(itemId);
      const item = allFoods.find(f => f._id === itemId);
      if (item && isConsultant) {
        setEditingItem({ ...item });
      }
    }
  };

  const handleInputChange = (field: keyof Food, value: any) => {
    if (editingItem) {
      setEditingItem({
        ...editingItem,
        [field]: value
      });
    }
  };

  const handleSave = async () => {
    if (!editingItem) return;

    try {
      await api.put(`/libraries/foods/${editingItem._id}`, editingItem);
      toast.success('Food updated successfully');
      
      // Update local data and cache
      const updatedFoods = allFoods.map(item =>
        item._id === editingItem._id ? editingItem : item
      );
      setAllFoods(updatedFoods);
      
      // Update cache as well
      if (foodsCache) {
        foodsCache = updatedFoods;
      }
      
      setExpandedItem(null);
      setEditingItem(null);
    } catch (error) {
      console.error('Failed to update food:', error);
      toast.error('Failed to update food');
    }
  };

  const handleCancel = () => {
    setEditingItem(null);
    setExpandedItem(null);
  };

  const getDietaryLabels = (item: Food) => {
    const labels = [];
    if (item.isVegan) labels.push('Vegan');
    if (item.isVegetarian) labels.push('Vegetarian');
    if (item.isGlutenFree) labels.push('Gluten-Free');
    if (item.isDairyFree) labels.push('Dairy-Free');
    return labels;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <Link to="/libraries" className="text-blue-600 text-sm font-medium">
            ← Libraries
          </Link>
          {isConsultant && (
            <button
              onClick={() => {/* TODO: Add new food modal */}}
              className="bg-blue-600 text-white px-3 py-1.5 rounded-md text-sm font-medium"
            >
              + Add
            </button>
          )}
        </div>
        <h1 className="text-xl font-bold text-gray-900 mt-2">Foods</h1>
        <p className="text-sm text-gray-600">
          {searchTerm && searchTerm.length >= 3 
            ? `${filteredFoods.length} of ${allFoods.length} items`
            : `${allFoods.length} items`
          }
        </p>
      </div>

      {/* Mobile Search - Sticky positioned below top bar */}
      <div className="sticky top-16 z-30 px-4 py-3 bg-white border-b border-gray-200 shadow-lg">
        <div className="relative">
          <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder={searchTerm.length > 0 && searchTerm.length < 3 ? "Type 3+ characters to search..." : "Search foods..."}
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className={`w-full pl-9 pr-4 py-2 border-0 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:bg-white ${
              searchTerm.length > 0 && searchTerm.length < 3 
                ? 'bg-yellow-50 text-yellow-700' 
                : 'bg-gray-50'
            }`}
          />
        </div>
      </div>

      {/* Food List */}
      {filteredFoods.length === 0 ? (
        <div className="text-center py-8 px-4">
          <ShieldCheckIcon className="mx-auto h-10 w-10 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No foods found</h3>
          <p className="mt-1 text-xs text-gray-500">
            {searchTerm
              ? 'Try adjusting your search'
              : 'Get started by adding foods.'}
          </p>
        </div>
      ) : (
        <div className="bg-white divide-y divide-gray-100">
          {filteredFoods.map((item) => (
            <div key={item._id}>
              {/* Mobile List Item */}
              <div
                onClick={() => toggleExpanded(item._id)}
                className="px-4 py-3 active:bg-gray-50 touch-manipulation"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-gray-900 truncate">{item.name}</h3>
                  </div>
                  {isConsultant && (
                    <PencilIcon className="h-4 w-4 text-gray-400 flex-shrink-0 ml-2" />
                  )}
                </div>
              </div>

              {/* Mobile Expanded Section */}
              {expandedItem === item._id && (
                <div className="px-4 py-4 bg-gray-50 border-t">
                  {isConsultant && editingItem ? (
                    /* Mobile Edit Form */
                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
                        <input
                          type="text"
                          value={editingItem.name}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Brand</label>
                          <input
                            type="text"
                            value={editingItem.brand || ''}
                            onChange={(e) => handleInputChange('brand', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
                          <input
                            type="text"
                            value={editingItem.category || ''}
                            onChange={(e) => handleInputChange('category', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Serving Size</label>
                          <input
                            type="number"
                            step="0.1"
                            value={editingItem.servingSize || ''}
                            onChange={(e) => handleInputChange('servingSize', parseFloat(e.target.value) || undefined)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Unit</label>
                          <input
                            type="text"
                            value={editingItem.servingMeasure || ''}
                            onChange={(e) => handleInputChange('servingMeasure', e.target.value)}
                            placeholder="cup, oz, g"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Calories</label>
                          <input
                            type="number"
                            value={editingItem.calories || ''}
                            onChange={(e) => handleInputChange('calories', parseInt(e.target.value) || undefined)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Protein (g)</label>
                          <input
                            type="number"
                            step="0.1"
                            value={editingItem.protein || ''}
                            onChange={(e) => handleInputChange('protein', parseFloat(e.target.value) || undefined)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Fat (g)</label>
                          <input
                            type="number"
                            step="0.1"
                            value={editingItem.fat || ''}
                            onChange={(e) => handleInputChange('fat', parseFloat(e.target.value) || undefined)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Carbs (g)</label>
                          <input
                            type="number"
                            step="0.1"
                            value={editingItem.totalCarbs || ''}
                            onChange={(e) => handleInputChange('totalCarbs', parseFloat(e.target.value) || undefined)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                          value={editingItem.description || ''}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="block text-xs font-medium text-gray-700">Dietary Labels</label>
                        <div className="grid grid-cols-2 gap-2">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={editingItem.isVegan || false}
                              onChange={(e) => handleInputChange('isVegan', e.target.checked)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-sm text-gray-700">Vegan</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={editingItem.isVegetarian || false}
                              onChange={(e) => handleInputChange('isVegetarian', e.target.checked)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-sm text-gray-700">Vegetarian</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={editingItem.isGlutenFree || false}
                              onChange={(e) => handleInputChange('isGlutenFree', e.target.checked)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-sm text-gray-700">Gluten-Free</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={editingItem.isDairyFree || false}
                              onChange={(e) => handleInputChange('isDairyFree', e.target.checked)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-sm text-gray-700">Dairy-Free</span>
                          </label>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="block text-xs font-medium text-gray-700">Status</label>
                        <div className="flex flex-wrap gap-3">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={editingItem.approved || false}
                              onChange={(e) => handleInputChange('approved', e.target.checked)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-sm text-gray-700">Approved</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={editingItem.isPublic || false}
                              onChange={(e) => handleInputChange('isPublic', e.target.checked)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-sm text-gray-700">Public</span>
                          </label>
                        </div>
                      </div>

                      <div className="flex space-x-3 pt-2">
                        <button
                          onClick={handleCancel}
                          className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white active:bg-gray-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSave}
                          className="flex-1 py-2 px-4 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 active:bg-blue-700"
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Mobile View Details */
                    <div className="space-y-3">
                      <div className="grid grid-cols-4 gap-2 text-center">
                        {item.calories && (
                          <div className="bg-blue-50 rounded-lg p-2">
                            <div className="text-sm font-medium text-blue-900">{item.calories}</div>
                            <div className="text-xs text-blue-600">cal</div>
                          </div>
                        )}
                        {item.protein && (
                          <div className="bg-green-50 rounded-lg p-2">
                            <div className="text-sm font-medium text-green-900">{item.protein}g</div>
                            <div className="text-xs text-green-600">protein</div>
                          </div>
                        )}
                        {item.totalCarbs && (
                          <div className="bg-orange-50 rounded-lg p-2">
                            <div className="text-sm font-medium text-orange-900">{item.totalCarbs}g</div>
                            <div className="text-xs text-orange-600">carbs</div>
                          </div>
                        )}
                        {item.fat && (
                          <div className="bg-purple-50 rounded-lg p-2">
                            <div className="text-sm font-medium text-purple-900">{item.fat}g</div>
                            <div className="text-xs text-purple-600">fat</div>
                          </div>
                        )}
                      </div>

                      {(item.brand || item.category) && (
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          {item.brand && (
                            <div>
                              <span className="text-xs text-gray-500 block">Brand</span>
                              <span className="text-gray-900">{item.brand}</span>
                            </div>
                          )}
                          {item.category && (
                            <div>
                              <span className="text-xs text-gray-500 block">Category</span>
                              <span className="text-gray-900">{item.category}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {(item.servingSize || item.servingMeasure) && (
                        <div>
                          <span className="text-xs text-gray-500 block">Serving Size</span>
                          <span className="text-sm text-gray-900">
                            {item.servingSize} {item.servingMeasure}
                          </span>
                        </div>
                      )}

                      {getDietaryLabels(item).length > 0 && (
                        <div>
                          <span className="text-xs text-gray-500 block mb-1">Dietary</span>
                          <div className="flex flex-wrap gap-1">
                            {getDietaryLabels(item).map((label, index) => (
                              <span key={index} className="inline-block bg-green-100 text-green-700 px-2 py-1 rounded text-xs">
                                {label}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {item.description && (
                        <div>
                          <span className="text-xs text-gray-500 block mb-1">Description</span>
                          <p className="text-sm text-gray-900">{item.description}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FoodLibrary;